import "dayjs/locale/ja";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/types";
import { Button, Form, Input, InputNumber, Select, Table } from "antd";
import { useEffect, useState } from "react";
import { useCreateServiceCenterServiceTypeMutation, useCreateServiceTypeMutation, useDeleteServiceCenterServiceTypeMutation, useDeleteServiceTypeMutation, useGetServiceTypesQuery, useLazyGetServiceTypesQuery, useUpdateServiceCenterServiceTypeMutation, useUpdateServiceTypeMutation } from "../../../services/serviceType";
import { Actions } from "../../../shared/constants";
import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import tableUtils from "../../../utils/tableUtils";
import HrnModal from "../../../components/HrnModal";
import validateMessages, { requiredRule } from "../../../utils/validate";
import hrnUtils from "../../../utils/hrnUtils";
import { useLazyGetServiceCentersQuery } from "../../../services/serviceCenter";
import useScreenSize from "../../../hooks/useScreenSize";

const ServiceTypesColumn: any[] = [
    {
        key: "name",
        header: "Name",
        className: " font-medium",
        responsive: ["xs"],
        fn: (value: any, record: any) => (<>
            <div className="text-primary">{record.name}</div>
            {record.price && <div>{record.price} BDT</div>}
            {record.defaultAllocatedTime && <div>{record.defaultAllocatedTime} Minutes</div>}
        </>)
    },
    {
        key: "name",
        header: "Name",
        className: "text-primary font-medium",
        responsive: ["sm", "md", "lg", "xl", "xxl"]
    },
    {
        key: "price",
        header: "Fee/Price",
        responsive: ["sm", "md", "lg", "xl", "xxl"],
        className: "font-medium",
        fn: (value: any, record: any) => `${value} BDT`
    },
    {
        key: "defaultAllocatedTime",
        header: "Allocated Time",
        responsive: ["sm", "md", "lg", "xl", "xxl"],
        className: "font-medium",
        fn: (value: any, record: any) => `${value} Minutes`
    },
];

const ServiceType = ({ companyId, insideTab }: { companyId?: string, insideTab?: boolean }) => {
    const { loggedInUser } = useSelector((state: RootState) => state.global);
    const { isMobile } = useScreenSize();
    const [serviceTypeForm] = Form.useForm();
    const [scServiceTypeForm] = Form.useForm();
    const [open, setOpen] = useState(false);
    const [openSc, setOpenSc] = useState(false);
    const [mode, setMode] = useState("");
    const [editId, setEditId] = useState("");
    const [serviceCenterId, setServiceCenterId] = useState<any>(null);
    const [serviceCenters, setServiceCenters] = useState<any[]>([]);
    const [getServiceCenters, getServiceCentersResult] = useLazyGetServiceCentersQuery();
    const [getSCServiceTypes, getSCServiceTypesResult] = useLazyGetServiceTypesQuery();

    const onServiceCenterChanged = (value: string) => {
        setServiceCenterId(value);
        getSCServiceTypes({ serviceCenterId: value }).unwrap();
    };

    useEffect(() => {
        getServiceCenters({ companyId: companyId || loggedInUser?.currentCompany?.id }).unwrap();
    }, [companyId, loggedInUser?.currentCompany?.id]);

    useEffect(() => {
        if (getServiceCentersResult.isSuccess) {
            const sCenters = getServiceCentersResult.data || [];
            setServiceCenters(sCenters);
            if (sCenters?.[0]) {
                setServiceCenterId(sCenters[0].id);
                onServiceCenterChanged(sCenters[0].id);
            }
        }
    }, [getServiceCentersResult.isFetching]);

    const {
        data: serviceTypes,
        isFetching: isSTFetching,
        isSuccess: isSTSuccess,
    } = useGetServiceTypesQuery({
        companyId: companyId || loggedInUser?.currentCompany?.id
    });

    const [createServiceType, createSTResult] = useCreateServiceTypeMutation();
    const [updateServiceType, updateSTResult] = useUpdateServiceTypeMutation();
    const [deleteServiceType] = useDeleteServiceTypeMutation();

    const showModal = () => {
        setMode(Actions.CREATE);
        setOpen(true);
    };

    const hideModal = () => {
        setOpen(false);
        serviceTypeForm.resetFields();
        setMode("");
    };

    const onSave = async () => {
        try {
            await serviceTypeForm.validateFields();
        } catch (errorInfo) {
            return;
        }

        const formFields = serviceTypeForm.getFieldsValue();

        if (mode === Actions.CREATE) {
            try {
                await createServiceType({
                    companyId: companyId || loggedInUser?.currentCompany?.id,
                    body: formFields
                }).unwrap();
                setOpen(false);
                serviceTypeForm.resetFields();
            } catch (e) { }
        } else {
            try {
                await updateServiceType({
                    companyId: companyId || loggedInUser?.currentCompany?.id,
                    id: editId,
                    body: formFields
                }).unwrap();
                setOpen(false);
                serviceTypeForm.resetFields();
            } catch (e) { }
        }
    };

    const handleEdit = (record: any) => {
        setMode(Actions.EDIT);
        setOpen(true);
        if (record.id) setEditId(record.id);
        serviceTypeForm.setFieldsValue(record);
    };

    const handleDelete = async (record: any) => {
        try {
            await deleteServiceType({
                companyId: loggedInUser?.currentCompany?.id,
                id: record.id
            }).unwrap();
        } catch (e) { }
    };

    const [createScServiceType, createScServiceTypeResult] = useCreateServiceCenterServiceTypeMutation();
    const [updateScServiceType, updateScServiceTypeResult] = useUpdateServiceCenterServiceTypeMutation();
    const [deleteScServiceType] = useDeleteServiceCenterServiceTypeMutation();

    const showScModal = () => {
        setMode(Actions.CREATE);
        setOpenSc(true);
    };

    const hideScModal = () => {
        setOpenSc(false);
        scServiceTypeForm.resetFields();
        setMode("");
    };

    const onSaveSc = async () => {
        try {
            await scServiceTypeForm.validateFields();
        } catch (errorInfo) {
            return;
        }

        const formFields = scServiceTypeForm.getFieldsValue();

        if (mode === Actions.CREATE) {
            try {
                await createScServiceType({
                    serviceCenterId,
                    body: formFields
                }).unwrap();
                setOpenSc(false);
                scServiceTypeForm.resetFields();
            } catch (e) { }
        } else {
            try {
                await updateScServiceType({
                    serviceCenterId,
                    id: editId,
                    body: formFields
                }).unwrap();
                setOpenSc(false);
                scServiceTypeForm.resetFields();
            } catch (e) { }
        }
    };

    const handleScEdit = (record: any) => {
        setMode(Actions.EDIT);
        setOpenSc(true);
        if (record.id) setEditId(record.id);
        scServiceTypeForm.setFieldsValue({ ...record, serviceTypeId: record.id });
    };

    const handleScDelete = async (record: any) => {
        try {
            await deleteScServiceType({
                serviceCenterId,
                id: record.id
            }).unwrap();
        } catch (e) { }
    };

    const onServiceTypeChanged = (value: string) => {
        if (mode === Actions.CREATE) {
            const type = serviceTypes.find((x: any) => x.id === value);
            scServiceTypeForm.setFieldValue("price", type?.price);
            scServiceTypeForm.setFieldValue("defaultAllocatedTime", type?.defaultAllocatedTime);
        }
    };

    return (
        <>
            <div className="flex justify-between items-center gap-3 pb-1">
                <div className="text-headline">{!insideTab ? "Service Types" : ""}</div>
                <Button type="primary" className="flex-end" onClick={showModal}>
                    <PlusOutlined /> Add
                </Button>
            </div>
            <div className="w-full">
                <Table
                    rowKey="id"
                    showHeader={insideTab}
                    columns={tableUtils.buildColumns(
                        isSTSuccess ? serviceTypes : [],
                        ServiceTypesColumn,
                        tableUtils.editDeleteActionColumns(handleEdit, handleDelete)
                    )}
                    dataSource={serviceTypes}
                    size="middle"
                    className="ant_table"
                    loading={{
                        spinning: isSTFetching as boolean,
                        indicator: <LoadingOutlined style={{ fontSize: 34 }} />,
                    }}
                    pagination={false}
                />
            </div>
            <div>
                <HrnModal
                    open={open}
                    title={`${mode === Actions.CREATE ? "Add" : "Edit"} Service Type`}
                    width={520}
                    isLoading={
                        (createSTResult.isLoading ||
                            updateSTResult.isLoading) as boolean
                    }
                    onCancel={hideModal}
                    onSave={onSave}
                >
                    <div className="mt-6">
                        <Form
                            form={serviceTypeForm}
                            layout="vertical"
                            name="serviceTypeForm"
                            validateMessages={validateMessages}
                        >
                            <div className="mb-5">
                                <Form.Item name="name" label={<>Name<span style={{ color: "red", marginLeft: "2px" }}>*</span></>} rules={[requiredRule]}>
                                    <Input size="large" placeholder={"Name"} />
                                </Form.Item>
                                <Form.Item name="price" label={<>Service Price</>} rules={[]}>
                                    <InputNumber inputMode="decimal" size="large" placeholder={"Price in BDT"} className="w-[100%]" suffix="BDT" />
                                </Form.Item>
                                <Form.Item name="defaultAllocatedTime" label={<>Default Allocated Time</>} rules={[]}>
                                    <InputNumber inputMode="numeric" size="large" placeholder={"Time in minutes"} className="w-[100%]" suffix="Minutes" />
                                </Form.Item>
                            </div>
                        </Form>
                    </div>
                </HrnModal>
            </div>
            {isSTSuccess && serviceTypes?.length > 0 && <>
                <div className="flex justify-between items-center gap-3 pb-1 mt-4 mb-2">
                    <div className="text-headline">{"Service Center's Service Types"}</div>
                </div>
                <div className="flex justify-between items-center gap-3 pb-1">
                    {!isMobile && <div className="whitespace-nowrap">Service Center:</div>}
                    <Select
                        style={{ width: "calc(100% - 70px)" }}
                        placeholder={"Service Center"}
                        options={serviceCenters.map((x: any) => ({ label: x.name, value: x.id }))}
                        showSearch
                        filterOption={hrnUtils.dropdownFilterOption}
                        showAction={["focus"]}
                        onChange={onServiceCenterChanged}
                        value={serviceCenterId}
                    />
                    <Button type="primary" className="w-[70px]" onClick={showScModal}>
                        <PlusOutlined /> Add
                    </Button>
                </div>
                <div className="w-full">
                    <Table
                        rowKey="id"
                        showHeader={insideTab}
                        columns={tableUtils.buildColumns(
                            getSCServiceTypesResult.isSuccess ? getSCServiceTypesResult.data : [],
                            ServiceTypesColumn,
                            tableUtils.editDeleteActionColumns(handleScEdit, handleScDelete)
                        )}
                        dataSource={getSCServiceTypesResult?.data || []}
                        size="middle"
                        className="ant_table"
                        loading={{
                            spinning: isSTFetching as boolean,
                            indicator: <LoadingOutlined style={{ fontSize: 34 }} />,
                        }}
                        pagination={false}
                    />
                </div>
                <div>
                    <HrnModal
                        open={openSc}
                        title={`${serviceCenters.find(x => x.id === serviceCenterId)?.name}`}
                        width={520}
                        isLoading={
                            (createScServiceTypeResult.isLoading ||
                                updateScServiceTypeResult.isLoading) as boolean
                        }
                        onCancel={hideScModal}
                        onSave={onSaveSc}
                    >
                        <div className="mt-6">
                            <Form
                                form={scServiceTypeForm}
                                layout="vertical"
                                name="scServiceTypeForm"
                                validateMessages={validateMessages}
                            >
                                <div className="mb-5">
                                    <Form.Item name="serviceTypeId" label={<>{"Service Type"}<span style={{ color: "red", marginLeft: "2px" }}>*</span></>} rules={[requiredRule]}>
                                        <Select disabled={mode === Actions.EDIT} onChange={onServiceTypeChanged} placeholder={"Service Type"} options={serviceTypes.map((x: any) => ({ label: x.name, value: x.id }))} />
                                    </Form.Item>
                                    <Form.Item name="price" label={<>{"Service Price"}<span style={{ color: "red", marginLeft: "2px" }}>*</span></>} rules={[requiredRule]}>
                                        <InputNumber inputMode="decimal" size="large" placeholder={"Price in BDT"} className="w-[100%]" suffix="BDT" />
                                    </Form.Item>
                                    <Form.Item name="defaultAllocatedTime" label={<>Default Allocated Time</>} rules={[]}>
                                        <InputNumber inputMode="numeric" size="large" placeholder={"Time in minutes"} className="w-[100%]" suffix="Minutes" />
                                    </Form.Item>
                                </div>
                            </Form>
                        </div>
                    </HrnModal>
                </div>
            </>}
        </>
    );
};

export default ServiceType;